import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { Flex, Box, Button, Title, Text, MantineProvider, createTheme } from "@mantine/core";
import Link from "next/link";
import { colors } from "@/theme";
import { clearCookie } from "@/utils/auth";
import { mantineTheme } from "@/theme/mantineTheme";
import loggingService from "@/services/logging-service";

const theme = createTheme(mantineTheme);

/**
 * Full-page "Something went wrong" fallback. Renders its own MantineProvider because it
 * can be shown from outside (or above) the normal provider tree. Currently not wired up —
 * errors are swallowed silently. Re-enable in RouterErrorFallback and main.tsx once
 * the error noise is tackled.
 */
export const AppErrorFallback = ({ error, componentStack }: { error?: unknown; componentStack?: string }) => {
    useEffect(() => {
        loggingService.logClientCrash(error, componentStack);
    }, []);

let handleRedirectToLogin = async () => {
    try {
        const { queryClient } = await import('@/src/query-client');
        if (queryClient) {
            queryClient.clear();
        }
    } catch (err) {
        console.warn('Failed to clear React Query cache before logout:', err);
    }

    clearCookie(true);
    window.location.href = '/login';
}

return <MantineProvider theme={theme}>
    <Box w={"100vw"} h={"100vh"} display={"block"} >
        <Flex pl={'20px'} py={'5px'} w={'100%'} h={'40px'} bg={colors.bluePrimary}>
             <div className="logo">
                            <Link
                                href={"/"}
                                style={{ textDecoration: 'none' }}
                             >
                                <span className="logo-big">
                                    <img src="/logo-type-white.svg" alt="ServCraft" />
                                </span>
                            </Link>
                       </div>
            </Flex>
        <Flex w={'100%'} h={'calc(100vh - 40px)'}
        direction={"column"} align={"center"} justify={"center"} gap={"md"}>
      <img src='/icons/something-went-wrong.svg' alt='Error' />
       <Title size={"xl"} >Something went wrong</Title>
       <Text size={'sm'}>Head back to login and try again</Text>
       <Button color="scBlue" onClick={handleRedirectToLogin}>Go to Login</Button>
        </Flex>
   </Box>
    </MantineProvider>
}

/**
 * Used as TanStack Router's `defaultErrorComponent`. Captures the error to Sentry
 * silently — no fallback UI is shown so users see a blank content area rather than
 * an error page. Re-enable AppErrorFallback here once the noise is tackled.
 */
export function RouterErrorFallback({ error }: { error: unknown }) {
    useEffect(() => {
        Sentry.captureException(error);
        loggingService.logClientCrash(error);
    }, [error])
    return null
}
